import React, { useEffect } from "react";
import Loader from "../../../components/loader";
import { Link } from "react-router-dom";
import CranesByOrderForm from "../../../forms/CranesByOrderForm";
import { useFormik } from "formik";
import ReportByCranesSchema from "../../../validations/ReportByCranesSchema";
import axiosClient from "../../../config/axios";
import { ReportByCranesOrders } from "../../../models/ReportByCranesOrders";
import { toCamelCase } from "../../../utils";
import TableCranesByOrder from "../../../components/reports/TableCranesByOrder";
import moment from "moment";

const CranesByOrder = () => {
  const [loading, setLoading] = React.useState(true);
  const [records, setRecords] = React.useState([]);
  const [cranes, setCranes] = React.useState([]);
  const [status, setStatus] = React.useState([]);
  const [orders, setOrders] = React.useState([]);
  const [operators, setOperators] = React.useState([]);
  const [priceRanges, setPriceRanges] = React.useState([]);

  const hasDataLoaded = React.useRef(false);

  const fetchData = async () => {
    try {
      const response = await axiosClient.get("/cranes/all");
      const formattedOptions = [
        { value: "all", label: "Todas las opciones" },
        ...response.data.data.map((crane) => ({
          value: crane.id,
          label: toCamelCase(crane.crane_number + ' ' + crane.description),
        })),
      ];

      setCranes(formattedOptions);

      const responseOperators = await axiosClient.get("/users/allOperators-active");
      const formattedOptionsOperators = [
        { value: "all", label: "Todas las opciones" },
        ...responseOperators.data.data.map((operator) => ({
          value: operator.id,
          label: toCamelCase(operator.name + " " + operator.first_surname),
        })),
      ];

      setOperators(formattedOptionsOperators);

      const responseStatus = await axiosClient.get("/orderStatuses/allWithOrderCount");
      const formattedOptionsStatus = [
        { value: "all", label: "Todas las opciones" },
        ...responseStatus.data.data.map((operator) => ({
          value: operator.id,
          label: operator.order_status,
        })),
      ];

      setStatus(formattedOptionsStatus);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...ReportByCranesOrders(),
      orderType: "Normal",
    },
    validationSchema: ReportByCranesSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const adjustedStart = moment(values.start).format("YYYY-MM-DD");
        const adjustedEnd = moment(values.end).format("YYYY-MM-DD");
        const dataTable = [];
        if (values.orderType === "Normal" || values.orderType === "all") {
          const endpointNormal = `/reports/cranes-by-order?start=${adjustedStart}&end=${adjustedEnd}&status=${values.status}&crane_id=${values.crane}&id_operador=${values.operator}`;
          const { data: normalData } = await axiosClient.get(endpointNormal);
          normalData?.forEach((record) => {
            const { crane, operador, orders } = record;

            if (!orders || orders.length === 0) {
              dataTable.push({
                crane,
                operador,
                tipo: "Normal",
                subtotal: 0,
                orden_en_cero: true,
                totalDistance: 0,
                sin_orden: true,
              });
              return;
            }

            orders.forEach((order) => {
              const { subtotal, orden_en_cero, totalDistance, ...rest } = order;

              dataTable.push({
                ...rest,
                subtotal: orden_en_cero ? 0 : subtotal,
                orden_en_cero: orden_en_cero || false,
                crane,
                operador,
                totalDistance,
                tipo: "Normal",
              });
            });
          });
        }
        
        dataTable.sort(
          (a, b) =>
            new Date(a.fecha_hora_reporte) - new Date(b.fecha_hora_reporte)
        );

        // === Filtrar las órdenes según el tipo seleccionado ===
        const filteredOrders =
          values.orderType && values.orderType !== "all"
            ? dataTable.filter((order) => order.tipo === values.orderType)
            : dataTable;

        setOrders(filteredOrders);

        // === Agrupar por operador (para exportar a Excel) ===
        const groupedRecords = {};

        filteredOrders.forEach((order) => {
          const crane = order.Crane;
          const craneId = crane?.id;
          if (!craneId) return;

          if (!groupedRecords[craneId]) {
            groupedRecords[craneId] = {
              crane,
              orders: [],
            };
          }

          groupedRecords[craneId].orders.push({
            Order: {
              id: order.id,
              folio: order.folio,
              subtotal: order.subtotal,
              orden_en_cero: order.orden_en_cero || false,
              fecha_hora_reporte: order.fecha_hora_reporte,
              grua: order.grua,
              auxilio_vial: order.auxilio_vial,
              montacarga: order.montacarga,
              corresponsalia: order.corresponsalia,
              taxi: order.taxi,
              Crane: order.Crane,
              tipoServicio: order.tipoServicio,
              tipo: order.tipo,
              razon: order.razon,
              comisionPorGrua: order.comisionPorGrua,
              operadores: order.operadores,
            },
            totalDistance: order.totalDistance || 0,
          });
        });

        // === Convertir a arreglo y ordenar las órdenes dentro de cada operador ===
        const combinedRecords = Object.values(groupedRecords).map((record) => {
          record.orders.sort(
            (a, b) =>
              new Date(a.Order?.fecha_hora_reporte) -
              new Date(b.Order?.fecha_hora_reporte)
          );
          return record;
        });

        setRecords(combinedRecords);

        hasDataLoaded.current = true;
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }
  });

  useEffect(() => {
    fetchData();
    formik.handleSubmit();
  }, []);

  return (
    <React.Fragment>
      <Loader isLoading={loading} />
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Inicio</Link>
            </li>
            <li className="breadcrumb-item active">
              Reporte de gruas por ordenes
            </li>
          </ol>
          <h1 className="page-header mb-0">Reporte de gruas por ordenes</h1>
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-xl-12 card border-0 p-3">
          <CranesByOrderForm
            formik={formik}
            records={records}
            cranes={cranes}
            operators={operators}
            status={status}
            priceRanges={priceRanges}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-xl-12 card border-0 p-3">
          <TableCranesByOrder
            orders={orders}
            records={records}
            start={formik.values.start}
            end={formik.values.end}
          />
        </div>
      </div>
    </React.Fragment>
  )
}

export default CranesByOrder;